import { useQuery } from "@tanstack/react-query";
import axios from "./axios";

/**
 * Platform revenue KPIs.
 *
 * Every `*_cents` field is an integer in minor currency units. The API sends
 * cents rather than a formatted string or a float on purpose: floats
 * accumulate rounding error as soon as the client sums or compares them, and
 * a pre-formatted string cannot be re-aggregated or localised.
 */
export interface RevenuePoint {
  month: string; // "2026-07"
  total: number; // net cents collected that month
  invoices: number;
}

export interface RevenueByPlan {
  slug: string;
  name: string;
  mrr_cents: number;
  subscriptions: number;
  vehicles: number;
}

export interface PlatformRevenue {
  currency: string;

  /** Cash actually collected, net of VAT. */
  collected_total_cents: number;
  collected_this_month_cents: number;
  collected_last_month_cents: number;
  collected_growth_percent: number;
  paid_invoices: number;

  /** Normalised run-rate of contracted subscriptions. */
  mrr_cents: number;
  arr_cents: number;
  trial_mrr_cents: number;
  at_risk_cents: number;
  arpa_cents: number;

  paying_accounts: number;
  trialing_accounts: number;
  past_due_accounts: number;
  canceled_last_30d: number;

  outstanding_cents: number;
  outstanding_invoices: number;

  revenue_evolution: RevenuePoint[];
  by_plan: RevenueByPlan[];
}

export const usePlatformRevenue = (months = 12) => {
  const { data, error, ...res } = useQuery({
    queryKey: ["admin", "dashboard", "revenue", months],
    queryFn: async (): Promise<PlatformRevenue> => {
      const response = await axios.get("/admin/dashboard/revenue", {
        params: { months },
      });
      return response.data;
    },
    staleTime: 60000,
  });
  return { revenue: data, error, ...res };
};

/**
 * Format cents as a currency string.
 *
 * Divides by 100 only at the very last step, immediately before rendering, so
 * no intermediate float ever participates in arithmetic.
 */
export const formatCents = (
  cents: number | undefined,
  currency = "EUR",
  locale = "fr-FR"
): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format((cents ?? 0) / 100);

/**
 * Compact form for chart axes, where a full "1 234 567 EUR" label would
 * collide with its neighbour.
 */
export const formatCentsCompact = (
  cents: number | undefined,
  currency = "EUR",
  locale = "fr-FR"
): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format((cents ?? 0) / 100);
